import {
	CommunityPostDetailsViewModel,
	CommunityPostStatisticsModel,
	getReactions,
	followCommunityPost,
	unFollowCommunityPost
} from "client/Api/Communities/communityPostViewer";
import {
	ReactElement,
	ReadonlyRefObject,
	useCallback,
	useMemo,
	useState
} from "react";
import styles from "./PostViewer.module.less";
import {
	generateTrackingId,
	isNotNil,
	isNotNilOrWhiteSpace,
	setTrackingId
} from "client/Misc/utils";
import {
	Anchor,
	BusyIndicator,
	ConditionalWrapper,
	ExpandableText,
	Intersection,
	NoResults
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { ActionAndOthers, ActionKind } from "client_legacy/ndaw/actions";
import {
	AlertManager,
	NumberFormatter,
	useAlerts,
	useCallbackWithoutArguments,
	useSynchronizedRef,
	useUnmountedCancellation,
	useUserTimezone
} from "client/Hooks";
import { PostImages } from "./PostImages";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { useComposerDialog } from "./Composer/ComposerDialog";
import { CommunityPostOptionsModel } from "client/Api/Communities/communityPosts";
import { HubDetailsContentFooter } from "../HubDetailsContentFooter";
import { CommunityPostToolbar } from "./CommunityPostToolbar";
import { ReactionsDialog } from "client/Features/Reactions/ReactionsDialog/ReactionsDialog";
import { ReactionCounterModel } from "client/Api/Hub/common";
import { useHubStatisticsFormatter } from "../useHubStatisticsFormatter";
import { TimeZone } from "client/DateAndTime/timezones";
import { formatUnit } from "client/Misc/cultureSpecificUtils";
import { PostHeader, PostHeaderActionContext } from "./PostHeader";
import { RawHtmlWithHoverableMentions } from "../RawHtmlWithHoverableMentions/RawHtmlWithHoverableMentions";
import { PollViewer } from "./PollViewer/PollViewer";
import { usePollData } from "./usePollData";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { LinkedDocumentsViewer } from "../LinkedDocumentsViewer";

export type PostViewerProps = {
	post: CommunityPostDetailsViewModel;
	onAfterUpdate: (post: CommunityPostDetailsViewModel) => void;
	onIsFollowingChanged: (isFollowing: boolean) => void;
	onDelete: () => void;
	onNavigateToCommunity?: () => void;
	postOptions: CommunityPostOptionsModel;
	onStatisticsRetrieved: (statistics: CommunityPostStatisticsModel) => void;
	shouldTruncateMessage?: boolean;
	onOpenPost?: (deeplink?: "comments") => void;
	onComment?: () => void;
	hasNoBottomMargin?: boolean;
};

export const PostViewer = (props: PostViewerProps): ReactElement => {
	const {
		post,
		onDelete,
		onAfterUpdate,
		onIsFollowingChanged,
		onNavigateToCommunity,
		postOptions,
		onStatisticsRetrieved,
		onOpenPost,
		onComment,
		shouldTruncateMessage = false,
		hasNoBottomMargin = false
	} = props;

	const [isReactionsDialogOpen, setIsReactionsDialogOpen] = useState<boolean>(false);
	const [isLoadingDocument, setIsLoadingDocument] = useState<boolean>(false);

	const [editDialog, openEditDialog] = useComposerDialog();
	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const userTimezone: TimeZone = useUserTimezone();

	const {
		status: pollDataStatus,
		pollData,
		fetchPollData,
		clearPollData,
		submitVote: submitPollVote,
		removeVote: removePollVote
	} = usePollData({
		postId: post.id,
		alertManager
	});

	const updatePostHandler = useCallback(
		async (): Promise<void> => {
			const { community, hasPoll } = post;

			if (hasPoll && pollDataStatus !== "ready") {
				return;
			}

			const result: TryResult<CommunityPostDetailsViewModel> = await openEditDialog({
				mode: {
					kind: "edit",
					community,
					id: post.id,
					images: post.images,
					poll: pollData,
					message: post.message ?? "",
					linkedDocuments: post.linkedDocuments
				},
				options: postOptions
			});

			if (!result.wasSuccessful) {
				return;
			}

			if (!result.result.hasPoll) {
				clearPollData();
			} else {
				fetchPollData();
			}

			onAfterUpdate(result.result);
		},
		[
			post,
			postOptions,
			pollDataStatus,
			pollData,
			onAfterUpdate,
			openEditDialog,
			clearPollData,
			fetchPollData
		]
	);

	const onOpenPostRef: ReadonlyRefObject<typeof onOpenPost> = useSynchronizedRef(onOpenPost);

	const actions = useMemo(
		(): readonly ActionAndOthers<PostHeaderActionContext>[] => {
			const actions: ActionAndOthers<PostHeaderActionContext>[] = [
				{
					canExecute: (c) => c.canEdit,
					kind: ActionKind.Regular,
					execute: updatePostHandler,
					name: "edit",
					title: localize("_Edit"),
					icon: "pen"
				},
				{
					canExecute: (c) => c.canEdit,
					kind: ActionKind.Regular,
					execute: () => onDelete(),
					name: "delete",
					title: localize("_Delete"),
					icon: "trash-can"
				}
			];

			const onOpenPost: (() => void) | undefined = onOpenPostRef.current;

			if (isNotNil(onOpenPost)) {
				actions.unshift({
					canExecute: () => true,
					kind: ActionKind.Regular,
					execute: () => onOpenPost(),
					name: "open",
					title: localize("_ViewPost"),
					icon: "eye"
				});
			}

			return actions;
		},
		[
			updatePostHandler,
			onDelete,
			onOpenPostRef
		]
	);

	const actionContext = useMemo(
		(): PostHeaderActionContext => {
			return {
				canEdit: post.isEditable
			};
		},
		[post.isEditable]
	);

	const trackingId: string = generateTrackingId("post-viewer", `|${post.id}`);

	const { statistics } = post;

	const getReactionCounts = useCallback(
		(): Promise<readonly ReactionCounterModel[]> => {
			return Promise.resolve(
				post.statistics
					.reactionCounters
			);
		},
		[post.statistics]
	);

	const isFollowingChangeHandler = useCallback(
		async (isFollowing: boolean): Promise<void> => {
			try {
				if (isFollowing) {
					await followCommunityPost(post.id, globalCancellation);
				} else {
					await unFollowCommunityPost(post.id, globalCancellation);
				}

				onIsFollowingChanged(isFollowing);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}
		},
		[
			alertManager,
			globalCancellation,
			post,
			onIsFollowingChanged
		]
	);

	const submitPollVoteHandler = useCallback(
		(optionIds: readonly number[]): Promise<TryResult<void>> => {
			return submitPollVote(optionIds, globalCancellation);
		},
		[globalCancellation, submitPollVote]
	);

	const removePollVoteHandler = useCallback(
		(): void => {
			removePollVote(globalCancellation);
		},
		[globalCancellation, removePollVote]
	);

	const canViewVoters = useMemo(
		(): boolean => {
			const {
				createdBy,
				isEditable
			} = post;

			const {
				currentUserIdentity
			} = userConfiguration();

			return isEditable
				|| currentUserIdentity.userId === createdBy?.userId;
		},
		[post]
	);

	const renderPollViewer = (): ReactElement | null => {
		if (pollDataStatus === "error") {
			return (
				<NoResults
					trackingId="unable-to-retrieve-poll"
					icon="gear"
					title={localize("_UnableToRetrievePoll")}
					message={localize("_ThePollCouldNotBeRetrieved")}
					onRetry={fetchPollData}
				/>
			);
		}

		return (
			<PollViewer
				postId={post.id}
				pollData={pollData}
				canViewVoters={canViewVoters}
				isBusy={pollDataStatus === "loading"}
				onSubmitVote={submitPollVoteHandler}
				onRemoveVote={removePollVoteHandler}
			/>
		);
	};

	return (
		<>
			<div
				{...setTrackingId(trackingId)}
			>
				{post.hasPoll && (
					<Intersection
						onEnter={fetchPollData}
						runOnce
					/>
				)}
				<PostHeader
					authorUserInfo={post.createdBy}
					actions={actions}
					actionContext={actionContext}
					community={post.community}
					publicationDate={post.createdOn}
					onNavigateToCommunity={onNavigateToCommunity}
					trackingId={generateTrackingId(trackingId, "__header")}
					timezone={userTimezone}
				/>

				{isNotNilOrWhiteSpace(post.message) && (
					<div
						className={styles.messageContainer}
					>
						<ConditionalWrapper
							condition={shouldTruncateMessage}
							wrapper={
								(children) => (
									<ExpandableText
										maxLines={3}
										text={<>{children}</>}
										shouldFadeWhenTruncated
									/>
								)
							}
						>
							<RawHtmlWithHoverableMentions html={post.message} />
						</ConditionalWrapper>
					</div>
				)}

				{post.hasPoll && (
					<>
						{isNotNilOrWhiteSpace(post.message) && (
							<hr className={styles.divider}/>
						)}
						<div className={styles.pollContainer}>
							{renderPollViewer()}
						</div>
					</>
				)}

				{post.images.length > 0 && (
					<div className={styles.imagesContainer}>
						<PostImages
							images={post.images}
							trackingId="images"
						/>
					</div>
				)}

				{post.linkedDocuments.length > 0 && (
					<>
						{isLoadingDocument && (
							<BusyIndicator/>
						)}

						<div className={styles.linkedDocumentsContainer}>
							<LinkedDocumentsViewer
								items={post.linkedDocuments}
								onChangeIsLoading={setIsLoadingDocument}
								moreButtonText={{
									expanded: localize("_ShowLessAttachments"),
									collapsed: localize("_ShowAllAttachments")
								}}
							/>
						</div>
					</>
				)}

				<HubDetailsContentFooter
					hasNoMargin={hasNoBottomMargin}
					stats={{
						reactions: {
							value: statistics.reactionCounters,
							onClick: statistics.reactionCounters.length > 0
								? () => setIsReactionsDialogOpen(true)
								: undefined,
						}
					}}
					rightSide={onOpenPost
						? (
							<CommentsCount
								amount={statistics.commentsCount}
								onClick={() => onOpenPost("comments")}
							/>
						)
						: undefined
					}
				>
					<CommunityPostToolbar
						onStatisticsRetrieved={onStatisticsRetrieved}
						isFollowing={post.isFollowing}
						postId={post.id}
						statistics={statistics}
						onComment={onComment}
						onIsFollowingChange={isFollowingChangeHandler}
					/>
				</HubDetailsContentFooter>
			</div>
			{editDialog}
			{isReactionsDialogOpen && (
				<ReactionsDialog
					getReactionCounts={getReactionCounts}
					getReactions={getReactions}
					itemId={post.id}
					onClose={() => setIsReactionsDialogOpen(false)}
				/>
			)}
		</>
	);
};
PostViewer.displayName="PostViewer";

type CommentsCountProps = {
	amount: number;
	onClick: () => void;
};

const CommentsCount = (props: CommentsCountProps): ReactElement => {
	const {
		amount,
		onClick: onClickFromProps
	} = props;

	const onClick: typeof onClickFromProps = useCallbackWithoutArguments(onClickFromProps);
	const statisticsFormatter: NumberFormatter = useHubStatisticsFormatter();

	const commentsCountText: string = useMemo(
		(): string => {
			return formatUnit({
				value: amount,
				singularForm: "_1Comment",
				pluralForm: "_NComments",
				valueFormatter: statisticsFormatter
			});
		},
		[amount, statisticsFormatter]
	);

	return (
		<div
			{...setTrackingId("comment-count")}
			className={styles.commentsCountContainer}
			title={`${amount}`}
		>
			{amount === 0
				? commentsCountText
				: (
					<Anchor
						className={styles.commentsLink}
						onClick={onClick}
					>
						{commentsCountText}
					</Anchor>
				)
			}
		</div>
	);
};
CommentsCount.displayName = "Comments";
